// Wire contract for the clubhouse-web-api game-pins endpoints
// (`/clubhouse/groups/{groupId}/hunts/{huntId}/game-pins`). The server owns
// reporterId, huntId, groupId, gamePinSource and images; the client only ever
// sends the fields in `GamePinBody`.

// The server returns the real pin type, including 'observed' (game seen but not
// shot); the web view treats all three distinctly rather than folding observed
// into wounded.
export type GamePinType = 'downed' | 'wounded' | 'observed'
export type GamePinGender = 'male' | 'female' | 'unknown'
export type GamePinAgeCategory = 'adult' | 'young' | 'unknown'
export type GamePinGameType = 'mammal' | 'bird'

export interface GamePinGameDto {
  name: string
  gender: GamePinGender
  ageCategory: GamePinAgeCategory
  gameType: GamePinGameType
}

export interface GamePinLocationDto {
  latitude: number
  longitude: number
}

export interface GamePinDto {
  id: string
  reporterId: string
  shooterId?: string | null
  pinType: GamePinType
  games: GamePinGameDto[]
  location: GamePinLocationDto
  date?: string | null
  comment?: string | null
  images?: string[]
  updatedAt?: string | null
}

export interface GamePinsResponse {
  pins: GamePinDto[]
  canManageAll: boolean
}

export interface GamePinBody {
  pinType: GamePinType
  games: GamePinGameDto[]
  location: GamePinLocationDto
  shooterId?: string | null
  comment?: string | null
  date?: string | null
}

/** The authenticated fetcher returned by `useApiFetch`. */
export type ApiCall = <T>(path: string, init?: { method?: string; body?: string }) => Promise<T>

const base = (groupId: string, huntId: string) =>
  `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/${encodeURIComponent(huntId)}/game-pins`

export function listHuntGamePins(call: ApiCall, groupId: string, huntId: string): Promise<GamePinsResponse> {
  return call<GamePinsResponse>(base(groupId, huntId))
}

export function createHuntGamePin(
  call: ApiCall,
  groupId: string,
  huntId: string,
  body: GamePinBody,
): Promise<GamePinDto> {
  return call<GamePinDto>(base(groupId, huntId), { method: 'POST', body: JSON.stringify(body) })
}

export function updateHuntGamePin(
  call: ApiCall,
  groupId: string,
  huntId: string,
  id: string,
  body: GamePinBody,
): Promise<GamePinDto> {
  return call<GamePinDto>(`${base(groupId, huntId)}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function deleteHuntGamePin(
  call: ApiCall,
  groupId: string,
  huntId: string,
  id: string,
): Promise<void> {
  return call<void>(`${base(groupId, huntId)}/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
